import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, ImagePlus, FileText, Star } from "lucide-react";
import { useAdminAuth } from "../hooks/useAdminAuth";
import { AdminNav } from "../components/admin/AdminNav";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  title: "", location: "", price: "", type: "urbano", beds: 1, area: "", garage: 0,
  description: "", imagesText: "",
  owner_name: "", owner_phone: "", commission: "", purchase_price: "", status: "disponivel", documentsText: "",
};

const inputCls =
  "h-12 w-full rounded-md bg-seed-off px-4 text-sm text-seed-deep outline-none transition-shadow placeholder:text-seed-deep/40 focus:shadow-[0_0_0_2px_#00D765]";

export default function AdminPanel() {
  const navigate = useNavigate();
  const { user, headers, logout } = useAdminAuth();
  const [items, setItems] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingDocs, setUploadingDocs] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/properties`);
      setItems(r.data);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user, load]);

  const openEdit = (p) =>
    setForm({ ...emptyForm, ...p, imagesText: p.images.join("\n"), documentsText: (p.documents || []).join("\n") });

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const imageUrls = () => form.imagesText.split("\n").map((s) => s.trim()).filter(Boolean);
  const docUrls = () => form.documentsText.split("\n").map((s) => s.trim()).filter(Boolean);

  const removeImage = (index) =>
    setForm((f) => ({ ...f, imagesText: imageUrls().filter((_, i) => i !== index).join("\n") }));

  const removeDoc = (index) =>
    setForm((f) => ({ ...f, documentsText: docUrls().filter((_, i) => i !== index).join("\n") }));

  const moveImage = (from, to) =>
    setForm((f) => {
      const urls = f.imagesText.split("\n").map((s) => s.trim()).filter(Boolean);
      if (from === to || from < 0 || to < 0 || from >= urls.length || to >= urls.length) return f;
      const [moved] = urls.splice(from, 1);
      urls.splice(to, 0, moved);
      return { ...f, imagesText: urls.join("\n") };
    });

  const handleAuthError = (err) => {
    if (err.response?.status === 401) {
      localStorage.removeItem("seed_admin_token");
      navigate("/admin/login");
      return true;
    }
    return false;
  };

  const uploadTo = (field) => async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    const setBusy = field === "imagesText" ? setUploading : setUploadingDocs;
    setBusy(true);
    try {
      const urls = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append("file", file);
        const r = await axios.post(`${API}/upload`, fd, { headers: headers() });
        urls.push(r.data.url);
      }
      setForm((f) => ({
        ...f,
        [field]: [...f[field].split("\n").map((s) => s.trim()).filter(Boolean), ...urls].join("\n"),
      }));
      toast.success(`${urls.length} arquivo(s) enviado(s)!`);
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error(err.response?.data?.detail || "Falha no envio.");
    } finally {
      setBusy(false);
    }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      location: form.location,
      price: form.price,
      type: form.type,
      beds: Number(form.beds) || 0,
      area: form.area,
      garage: Number(form.garage) || 0,
      description: form.description,
      images: imageUrls(),
      owner_name: form.owner_name || null,
      owner_phone: form.owner_phone || null,
      commission: form.commission || null,
      purchase_price: form.purchase_price || null,
      status: form.status,
      documents: docUrls(),
    };
    try {
      if (form.id) {
        await axios.put(`${API}/properties/${form.id}`, payload, { headers: headers() });
        toast.success("Imóvel atualizado!");
      } else {
        await axios.post(`${API}/properties`, payload, { headers: headers() });
        toast.success("Imóvel cadastrado!");
      }
      setForm(null);
      await load();
    } catch (err) {
      if (handleAuthError(err)) return;
      toast.error("Não foi possível salvar. Confira os campos.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.title}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await axios.delete(`${API}/properties/${p.id}`, { headers: headers() });
      toast.success("Imóvel excluído.");
      await load();
    } catch {
      toast.error("Não foi possível excluir.");
    }
  };

  const STATUS_LABEL = { disponivel: "Disponível", reservado: "Reservado", vendido: "Vendido" };

  return (
    <main className="mx-auto min-h-screen max-w-7xl px-6 pt-20 pb-24 lg:px-10" data-testid="admin-panel">
      <AdminNav user={user} onLogout={logout} />
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-seed-deep">Meus imóveis</h1>
          {items && items.length > 0 && (
            <p className="mt-1 text-xs font-semibold text-seed-deep/45" data-testid="admin-properties-summary">
              {(() => {
                const pl = (n, s, p) => `${n} ${n === 1 ? s : p}`;
                const disp = items.filter((p) => (p.status || "disponivel") === "disponivel").length;
                const res = items.filter((p) => p.status === "reservado").length;
                const ven = items.filter((p) => p.status === "vendido").length;
                return `${pl(items.length, "imóvel", "imóveis")} · ${pl(disp, "disponível", "disponíveis")} · ${pl(res, "reservado", "reservados")} · ${pl(ven, "vendido", "vendidos")}`;
              })()}
            </p>
          )}
        </div>
        <button
          onClick={() => setForm({ ...emptyForm })}
          data-testid="admin-new-property"
          className="flex items-center gap-2 rounded-full bg-seed-vivid px-6 py-3 font-heading text-sm font-bold text-seed-deep transition-transform duration-300 hover:scale-105"
        >
          <Plus size={16} strokeWidth={2.5} /> Novo imóvel
        </button>
      </div>

      {form && (
        <form onSubmit={save} className="mt-10 rounded-lg bg-white p-8 shadow-sm" data-testid="property-form">
          <div className="mb-6 flex items-center justify-between">
            <h2 className="font-heading text-xl font-bold text-seed-deep">
              {form.id ? "Editar imóvel" : "Cadastrar imóvel"}
            </h2>
            <button type="button" onClick={() => setForm(null)} data-testid="property-form-close" className="text-seed-deep/40 hover:text-seed-deep">
              <X size={20} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <input required placeholder="Título (ex.: Casa Moderna no Jardim)" value={form.title} onChange={set("title")} className={inputCls} data-testid="pf-title" />
            <input required placeholder="Localização (ex.: Uberlândia · MG)" value={form.location} onChange={set("location")} className={inputCls} data-testid="pf-location" />
            <input required placeholder="Preço (ex.: R$ 890.000)" value={form.price} onChange={set("price")} className={inputCls} data-testid="pf-price" />
            <select value={form.type} onChange={set("type")} className={inputCls} data-testid="pf-type">
              <option value="urbano">Urbano</option>
              <option value="rural">Rural</option>
            </select>
            <input required type="number" min="0" placeholder="Quartos" value={form.beds} onChange={set("beds")} className={inputCls} data-testid="pf-beds" />
            <input required placeholder="Área (ex.: 210 m² ou 48 ha)" value={form.area} onChange={set("area")} className={inputCls} data-testid="pf-area" />
            <input required type="number" min="0" placeholder="Vagas" value={form.garage} onChange={set("garage")} className={inputCls} data-testid="pf-garage" />
          </div>
          <textarea
            required
            rows={3}
            placeholder="Descrição do imóvel"
            value={form.description}
            onChange={set("description")}
            className={`${inputCls} mt-4 h-auto py-3`}
            data-testid="pf-description"
          />

          <div className="mt-4 rounded-md border border-dashed border-seed-salad/50 bg-seed-off/60 p-5">
            <label className="flex cursor-pointer items-center justify-center gap-3 text-sm font-bold text-seed-deep transition-colors hover:text-seed-salad" data-testid="pf-upload-button">
              <input type="file" hidden multiple accept="image/jpeg,image/png,image/webp,image/gif" onChange={uploadTo("imagesText")} data-testid="pf-upload-input" />
              <ImagePlus size={20} className="text-seed-salad" />
              {uploading ? "Enviando fotos..." : "Enviar fotos do computador (JPG, PNG, WEBP — máx. 10 MB cada)"}
            </label>
            {imageUrls().length > 0 && (
              <>
                <p className="mt-4 text-xs font-semibold text-seed-deep/50" data-testid="pf-order-hint">
                  Arraste as fotos para ordenar ou clique na estrela para definir a capa.
                </p>
                <div className="mt-3 flex flex-wrap gap-3" data-testid="pf-image-previews">
                  {imageUrls().map((url, i) => (
                    <div
                      key={`${url}-${i}`}
                      draggable
                      onDragStart={(e) => e.dataTransfer.setData("text/plain", String(i))}
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        moveImage(Number(e.dataTransfer.getData("text/plain")), i);
                      }}
                      data-testid={`pf-image-${i}`}
                      className="relative cursor-grab rounded-md transition-transform duration-200 hover:scale-105 active:cursor-grabbing"
                      title="Arraste para reordenar"
                    >
                      <img src={url} alt={`Foto ${i + 1}`} className="h-16 w-20 rounded-md object-cover" draggable={false} />
                      {i === 0 && (
                        <span className="absolute left-1 top-1 rounded-full bg-seed-vivid px-2 py-0.5 text-[9px] font-black uppercase tracking-wide text-seed-deep" data-testid="pf-cover-badge">
                          Capa
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => moveImage(i, 0)}
                        data-testid={`pf-cover-star-${i}`}
                        aria-label={i === 0 ? "Foto de capa" : `Definir foto ${i + 1} como capa`}
                        title={i === 0 ? "Esta é a capa" : "Definir como capa"}
                        className={`absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full shadow transition-colors ${i === 0 ? "bg-seed-vivid text-seed-deep" : "bg-white/85 text-seed-deep/50 hover:text-seed-deep"}`}
                      >
                        <Star size={11} fill={i === 0 ? "currentColor" : "none"} />
                      </button>
                      <button
                        type="button"
                        onClick={() => removeImage(i)}
                        data-testid={`pf-remove-image-${i}`}
                        aria-label={`Remover foto ${i + 1}`}
                        className="absolute -right-2 -top-2 flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-white shadow"
                      >
                        <X size={11} />
                      </button>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>

          <textarea
            required
            rows={3}
            placeholder="Fotos: envie pelo botão acima ou cole um link de imagem por linha"
            value={form.imagesText}
            onChange={set("imagesText")}
            className={`${inputCls} mt-4 h-auto py-3 font-mono text-xs`}
            data-testid="pf-images"
          />

          <div className="mt-8 rounded-md bg-seed-deep/[0.03] p-6" data-testid="pf-internal-section">
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.2em] text-seed-deep/50">
              Dados internos — não aparecem no site
            </p>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <input placeholder="Proprietário (nome)" value={form.owner_name} onChange={set("owner_name")} className={inputCls} data-testid="pf-owner-name" />
              <input placeholder="Telefone do proprietário" value={form.owner_phone} onChange={set("owner_phone")} className={inputCls} data-testid="pf-owner-phone" />
              <input placeholder="Comissão (ex.: 6% ou R$ 30.000)" value={form.commission} onChange={set("commission")} className={inputCls} data-testid="pf-commission" />
              <input placeholder="Preço de aquisição (ex.: R$ 700.000)" value={form.purchase_price} onChange={set("purchase_price")} className={inputCls} data-testid="pf-purchase-price" />
              <select value={form.status} onChange={set("status")} className={inputCls} data-testid="pf-status">
                <option value="disponivel">Disponível</option>
                <option value="reservado">Reservado</option>
                <option value="vendido">Vendido</option>
              </select>
            </div>
            <div className="mt-4 rounded-md border border-dashed border-seed-deep/20 bg-white p-5">
              <label className="flex cursor-pointer items-center justify-center gap-3 text-sm font-bold text-seed-deep transition-colors hover:text-seed-salad" data-testid="pf-docs-upload-button">
                <input type="file" hidden multiple accept="application/pdf,image/jpeg,image/png,image/webp" onChange={uploadTo("documentsText")} data-testid="pf-docs-upload-input" />
                <FileText size={18} className="text-seed-salad" />
                {uploadingDocs ? "Enviando documentos..." : "Anexar documentos (PDF ou imagem — escritura, matrícula, contratos)"}
              </label>
              {docUrls().length > 0 && (
                <div className="mt-4 space-y-2" data-testid="pf-docs-list">
                  {docUrls().map((url, i) => (
                    <div key={`${url}-${i}`} className="flex items-center justify-between gap-3 rounded-md bg-seed-off px-4 py-2.5">
                      <a href={url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 truncate text-xs font-semibold text-seed-deep hover:text-seed-salad">
                        <FileText size={13} className="shrink-0 text-seed-salad" /> Documento {i + 1}
                      </a>
                      <button type="button" onClick={() => removeDoc(i)} data-testid={`pf-remove-doc-${i}`} aria-label={`Remover documento ${i + 1}`} className="text-red-400 hover:text-red-600">
                        <X size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button
            type="submit"
            disabled={saving || uploading || uploadingDocs}
            data-testid="pf-save"
            className="mt-6 rounded-full bg-seed-deep px-10 py-4 font-heading text-sm font-bold text-seed-off transition-transform duration-300 hover:scale-[1.02] disabled:opacity-60"
          >
            {saving ? "Salvando..." : form.id ? "Salvar alterações" : "Cadastrar imóvel"}
          </button>
        </form>
      )}

      <div className="mt-10" data-testid="admin-property-list">
        {items === null ? (
          <div className="flex justify-center py-16">
            <div className="h-10 w-10 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
          </div>
        ) : items.length === 0 ? (
          <p className="py-16 text-center text-seed-deep/50">Nenhum imóvel cadastrado ainda.</p>
        ) : (
          <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
            {items.map((p) => (
              <div key={p.id} className="flex gap-4 rounded-lg bg-white p-4 shadow-sm" data-testid={`admin-item-${p.id.slice(0, 8)}`}>
                <img src={p.images[0]} alt={p.title} className="h-20 w-24 shrink-0 rounded-md object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate font-heading text-sm font-bold text-seed-deep">{p.title}</p>
                  <p className="mt-0.5 text-xs text-seed-deep/50">{p.location}</p>
                  <p className="mt-1 text-sm font-black text-seed-salad">{p.price}</p>
                  <p className="mt-0.5 text-[11px] font-bold uppercase tracking-wider text-seed-deep/40">
                    {p.type === "rural" ? "Rural" : "Urbano"} · {STATUS_LABEL[p.status] || "Disponível"}
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => openEdit(p)}
                    data-testid={`admin-edit-${p.id.slice(0, 8)}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-seed-vivid"
                    aria-label="Editar"
                  >
                    <Pencil size={15} />
                  </button>
                  <button
                    onClick={() => remove(p)}
                    data-testid={`admin-delete-${p.id.slice(0, 8)}`}
                    className="flex h-9 w-9 items-center justify-center rounded-full bg-seed-off text-seed-deep transition-colors hover:bg-red-500 hover:text-white"
                    aria-label="Excluir"
                  >
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}
